import { FC, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectBankAccounts,
  selectActiveCompany,
} from '@features/workspace/store/selectors';
import ConfirmDialog from '@features/workspace/components/dialogs/confirm-dialog';
import { setNotification } from '@store/features/notification';
import { setIsLoading } from '@store/features/loading';
import {
  TraderootAccountActionType,
  TraderootAccountStatusText,
} from '@utils/traderoot';
import SelectableCard from '@features/workspace/components/quick-links/selectable-card';
import QuickActionDialog from '@features/workspace/components/quick-links/quick-action-dialog';
import { NotFoundImage } from '@app/icons';
import {
  blockTraderootAccount,
  unblockTraderootAccount,
} from '@features/workspace/store/actions/update';
import { fetchBankAccounts } from '@features/workspace/store/actions';

import logger from '@app/services/logger';

const BlockUnblockAccountDialog: FC<any> = ({ open, onClose, action }) => {
  const dispatch = useAppDispatch();

  const companyId = useAppSelector(selectActiveCompany)?.id;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [, setError] = useState<null | string>(null);

  const filterOutStates = useMemo(() => {
    return [
      TraderootAccountStatusText.Inactive,
      TraderootAccountStatusText.New,
      TraderootAccountStatusText.Closed,
      TraderootAccountStatusText.NotAvailable,
      TraderootAccountStatusText.AwaitingDeposit,
      TraderootAccountStatusText.UnderInvestigation,
      TraderootAccountStatusText.Dormant,
      TraderootAccountStatusText.Deleted,
      action === TraderootAccountActionType.Block
        ? TraderootAccountStatusText.Frozen
        : TraderootAccountStatusText.Active,
    ];
  }, [action]);

  const bankAccounts = useAppSelector(selectBankAccounts)
    .filter((acc: any) => !filterOutStates.includes(acc.statusName))
    .sort((acc1: any, acc2: any) => acc1.name.localeCompare(acc2.name));

  useEffect(() => {
    if (open) {
      setSelectedAccountNumbers([]);
      // setBlockReason('')
      setError(null);
      setIsConfirmOpen(false);
    }
  }, [open]);

  const [selectedAccountNumbers, setSelectedAccountNumbers] = useState<
    Array<string>
  >([]);

  const getIsAccountSelected = (accNumber: string) =>
    selectedAccountNumbers.includes(accNumber);

  const onOk = () => {
    setIsConfirmOpen(true);
  };

  const onConfirm = async () => {
    try {
      dispatch(setIsLoading(true));
      if (action === TraderootAccountActionType.Block) {
        await Promise.all(
          selectedAccountNumbers.map((accountNumber: string) =>
            dispatch(
              blockTraderootAccount({
                companyId,
                accountNumber,
              })
            )
          )
        );
      } else {
        await Promise.all(
          selectedAccountNumbers.map((accountNumber: string) =>
            dispatch(
              unblockTraderootAccount({
                companyId,
                accountNumber,
              })
            )
          )
        );
      }
      // Refresh bank accounts list
      dispatch(fetchBankAccounts(null));

      dispatch(setIsLoading(false));
      dispatch(
        setNotification({
          message: `${
            selectedAccountNumbers.length > 1 ? 'Cards' : 'Card'
          } ${selectedAccountNumbers.join(', ')} successfully ${
            action === TraderootAccountActionType.Block
              ? 'suspended'
              : 'reactivated'
          }!`,
          variant: 'success',
        })
      );
      onClose();
      setIsConfirmOpen(false);
    } catch (err) {
      logger.error('Suspend/reactivate mastercard card request failed', err);
    }
  };

  const onSwitchBankAccount = (accNumber: string) => {
    if (selectedAccountNumbers.includes(accNumber)) {
      setSelectedAccountNumbers(
        selectedAccountNumbers.filter((val) => val !== accNumber)
      );
    } else {
      setSelectedAccountNumbers([...selectedAccountNumbers, accNumber]);
    }
  };

  const getHeaderText = () => {
    if (!bankAccounts.length) {
      return action === TraderootAccountActionType.Block
        ? 'There is no active accounts to suspend'
        : 'There is no suspended accounts to reactivate:';
    }

    return action === TraderootAccountActionType.Block
      ? 'Please select which accounts you want to suspend:'
      : 'Please select which accounts you want to reactivate:';
  };

  const confirmContent = useMemo(() => {
    const accDesc = `${
      selectedAccountNumbers.length > 1 ? selectedAccountNumbers.length : ''
    } ${
      selectedAccountNumbers.length > 1 ? 'accounts' : 'account'
    }: ${selectedAccountNumbers.join(', ')}`;

    return action === TraderootAccountActionType.Block
      ? `Are you sure you want to suspend ${accDesc}?`
      : `Are you sure you want to reactivate ${accDesc}?`;
  }, [action, selectedAccountNumbers]);

  const okText =
    action === TraderootAccountActionType.Block ? 'Suspend' : 'Reactivate';

  return (
    action && (
      <>
        <QuickActionDialog
          headerText={getHeaderText()}
          isOpen={open}
          okDisabled={!selectedAccountNumbers.length}
          okText={okText}
          onClose={onClose}
          onOk={onOk}
        >
          <Box display="flex" flexDirection="column">
            {!bankAccounts.length && (
              <Box display="flex" justifyContent="center">
                <NotFoundImage />
              </Box>
            )}
            {bankAccounts.map((acc: any) => (
              <SelectableCard
                key={acc.bankAccountNumber}
                isSelected={getIsAccountSelected(acc.bankAccountNumber)}
                onSwitchSelect={() =>
                  onSwitchBankAccount(acc.bankAccountNumber)
                }
              >
                {/* Card left side */}
                <Box display="block" flexDirection="column" flexGrow={1}>
                  <Box
                    display="flex"
                    style={{
                      color: '#478AAA',
                      fontSize: '12px',
                      lineHeight: '18px',
                      fontWeight: '700',
                    }}
                  >
                    {acc.name}
                  </Box>
                  <Box
                    display="flex"
                    style={{
                      color: '#838383',
                      fontSize: '12px',
                      lineHeight: '18px',
                    }}
                  >
                    {acc.bankAccountNumber}
                  </Box>
                </Box>
              </SelectableCard>
            ))}
          </Box>
        </QuickActionDialog>
        <ConfirmDialog
          content={confirmContent}
          open={isConfirmOpen}
          onCancel={() => setIsConfirmOpen(false)}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={onConfirm}
        />
      </>
    )
  );
};

export default BlockUnblockAccountDialog;
